/**
 * Helvetica Neue is the ONLY font family this app composes with.
 *
 * The AI and the style presets vary weight, size, tracking, case, and
 * alignment — never the family. `FONT_STACK` is the single source of truth and
 * is used by both the DOM chrome and the canvas renderer, so the preview and
 * the export cannot drift apart.
 *
 * Resolution order per weight (see src/styles/fonts.css):
 *   1. `local()` — the real Helvetica Neue installed on macOS/iOS.
 *   2. Licensed .woff2/.woff/.ttf you drop into /public/fonts (see its README).
 *   3. The Helvetica-family fallbacks at the end of the stack.
 */

export const FONT_FAMILY = 'HelveticaNeueApp';

export const FONT_STACK =
  '"HelveticaNeueApp", "Helvetica Neue", "HelveticaNeue", Helvetica, "Nimbus Sans", "Liberation Sans", Arial, sans-serif';

export interface WeightDef {
  value: number;
  label: string;
  /** File basename expected in /public/fonts, without extension. */
  file: string;
}

/**
 * Heavy (800/900) is not part of the base macOS Helvetica Neue install, so it
 * falls back to Bold via the @font-face src list rather than to another family.
 */
export const HN_WEIGHTS: WeightDef[] = [
  { value: 200, label: 'UltraLight', file: 'HelveticaNeue-UltraLight' },
  { value: 300, label: 'Light', file: 'HelveticaNeue-Light' },
  { value: 400, label: 'Regular', file: 'HelveticaNeue' },
  { value: 500, label: 'Medium', file: 'HelveticaNeue-Medium' },
  { value: 700, label: 'Bold', file: 'HelveticaNeue-Bold' },
  { value: 800, label: 'Heavy', file: 'HelveticaNeue-Heavy' },
];

export function weightLabel(value: number): string {
  let best = HN_WEIGHTS[0];
  for (const w of HN_WEIGHTS) {
    if (Math.abs(w.value - value) < Math.abs(best.value - value)) best = w;
  }
  return best.label;
}

/** Snap an arbitrary weight to the nearest weight the family actually ships. */
export function nearestWeight(value: number): number {
  let best = HN_WEIGHTS[0].value;
  for (const w of HN_WEIGHTS) {
    if (Math.abs(w.value - value) < Math.abs(best - value)) best = w.value;
  }
  return best;
}

export function fontString(
  weight: number,
  sizePx: number,
  family = FONT_STACK,
): string {
  return `${weight} ${sizePx}px ${family}`;
}

/**
 * Canvas will silently substitute a fallback if a face has not finished
 * loading, so every render path awaits this first.
 */
export async function ensureFontsLoaded(weights: number[]): Promise<void> {
  if (typeof document === 'undefined' || !('fonts' in document)) return;
  const unique = Array.from(new Set(weights.map(nearestWeight)));
  await Promise.all(
    unique.map((w) =>
      document.fonts
        .load(`${w} 64px "${FONT_FAMILY}"`)
        .catch(() => undefined),
    ),
  );
  await document.fonts.ready;
}

export interface FontStatus {
  /** True when a real Helvetica Neue face resolved (local or supplied file). */
  resolved: boolean;
  /** Which weights measurably differ from the generic fallback. */
  available: number[];
  detail: string;
}

/**
 * Width-comparison probe. `document.fonts.check()` reports true for a stack
 * that merely falls back, so we measure a glyph string that renders at
 * distinctly different widths in Helvetica Neue vs. the sans fallback.
 */
export function detectHelveticaNeue(): FontStatus {
  if (typeof document === 'undefined') {
    return { resolved: false, available: [], detail: 'no document' };
  }
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return { resolved: false, available: [], detail: 'no 2d context' };

  const probe = 'MWQ@1234567890 iljtf';
  const measure = (family: string, weight: number) => {
    ctx.font = `${weight} 96px ${family}`;
    return ctx.measureText(probe).width;
  };

  const available: number[] = [];
  for (const w of HN_WEIGHTS) {
    const sentinel = measure('monospace', w.value);
    const target = measure(`"${FONT_FAMILY}", monospace`, w.value);
    if (Math.abs(target - sentinel) > 0.5) available.push(w.value);
  }

  // Arial and Helvetica have near-identical metrics, so a stack that resolved
  // to Arial is indistinguishable by width alone. Report that honestly.
  const arial = measure('Arial', 400);
  const app = measure(`"${FONT_FAMILY}", monospace`, 400);
  const distinctFromArial = Math.abs(arial - app) > 0.5;

  if (available.length === 0) {
    return {
      resolved: false,
      available,
      detail: 'Helvetica Neue not found — using the Helvetica-family fallback.',
    };
  }
  return {
    resolved: true,
    available,
    detail: distinctFromArial
      ? `Helvetica Neue active (${available.length}/${HN_WEIGHTS.length} weights).`
      : `Helvetica-family face active (${available.length}/${HN_WEIGHTS.length} weights). Metrics match Arial, so this may be a substitute — add licensed files to /public/fonts to be certain.`,
  };
}
